import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import axios from 'axios'
import { User, AuthState } from '../types/auth'
import { authApi } from '../services/api/auth'

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>
  register: (userData: RegisterData) => Promise<void>
  logout: () => void
  verifyIdentity: (verificationData: IdentityVerificationData) => Promise<void>
  updateProfile: (profileData: Partial<User>) => Promise<void>
  isLoading: boolean
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
  dateOfBirth: string
  ssn: string
  address: {
    street: string
    city: string
    state: string
    zipCode: string
  }
}

interface IdentityVerificationData {
  documentType: 'drivers_license' | 'passport' | 'state_id'
  documentFront: File
  documentBack?: File
  selfie: File
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isIdentityVerified, setIsIdentityVerified] = useState(false)

  const parseAuthTokenIdentity = (token: string): { userId?: string; email?: string } | null => {
    try {
      const decoded = atob(token)
      if (decoded.includes(':')) {
        const [userId, email] = decoded.split(':')
        return { userId, email }
      }
    } catch {}

    try {
      if (!token.includes('.')) return null
      const payload = token.split('.')[1]
      if (!payload) return null
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
      const decodedPayload = atob(normalized)
      const parsed = JSON.parse(decodedPayload)
      const email = typeof parsed.email === 'string' ? parsed.email : undefined
      const userId = typeof parsed.userId === 'string' ? parsed.userId : (typeof parsed.sub === 'string' ? parsed.sub : undefined)
      return { userId, email }
    } catch {
      return null
    }
  }

  const normalizeUser = (rawUser: User): User => {
    return {
      ...rawUser,
      id: String(rawUser.id)
    }
  }

  useEffect(() => {
    if (!user) return
    const token = localStorage.getItem('auth_token')
    if (!token) return
    if (token.includes('.')) return
    const tokenIdentity = parseAuthTokenIdentity(token)
    if (!tokenIdentity?.email) return
    if (tokenIdentity.email === user.email) return
    try {
      localStorage.setItem('auth_token', btoa(`${user.id}:${user.email}`))
    } catch {}
  }, [user?.id, user?.email])

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          const tokenIdentity = parseAuthTokenIdentity(token)
          const userData = await authApi.getCurrentUser()
          const normalizedUser = normalizeUser(userData)
          if (tokenIdentity?.email && normalizedUser.email && tokenIdentity.email !== normalizedUser.email) {
            localStorage.removeItem('auth_token')
            setUser(null)
            setIsAuthenticated(false)
            setIsIdentityVerified(false)
            return
          }
          const demoIdentityVerified = normalizedUser.email === 'demo1@open1031.ai' && localStorage.getItem('demo_identity_verified') === 'true'
          setUser(demoIdentityVerified ? { ...normalizedUser, isIdentityVerified: true } : normalizedUser)
          setIsAuthenticated(true)
          setIsIdentityVerified(demoIdentityVerified ? true : normalizedUser.isIdentityVerified)
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        localStorage.removeItem('auth_token')
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      const response = await authApi.login(email, password)
      console.log('Login response:', { user: response.user, isAdmin: response.user.isAdmin })
      localStorage.setItem('auth_token', response.token)
      // Ensure user ID is a string
      const userWithStringId = {
        ...response.user,
        id: String(response.user.id)
      }
      setUser(userWithStringId)
      setIsAuthenticated(true)
      setIsIdentityVerified(response.user.isIdentityVerified)
    } catch (error: any) {
      console.error('Login error:', error)
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        throw new Error('Incorrect email or password.')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: RegisterData) => {
    try {
      setIsLoading(true)
      const response = await authApi.register(userData)
      localStorage.setItem('auth_token', response.token)
      const normalizedUser = normalizeUser(response.user)
      setUser(normalizedUser)
      setIsAuthenticated(true)
      setIsIdentityVerified(false)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    sessionStorage.removeItem('sales_hub_shown_demo1_session') // Clear demo1 flag
    setUser(null)
    setIsAuthenticated(false)
    setIsIdentityVerified(false)
  }

  const verifyIdentity = async (verificationData: IdentityVerificationData) => {
    if (import.meta.env.DEV && user?.email === 'demo1@open1031.ai') {
      localStorage.setItem('demo_identity_verified', 'true')
      setUser((previousUser) => {
        if (!previousUser) return previousUser
        return {
          ...previousUser,
          isIdentityVerified: true,
          isKycComplete: true
        }
      })
      setIsIdentityVerified(true)
      return
    }
    try {
      setIsLoading(true)
      const response = await authApi.verifyIdentity(verificationData)
      const token = localStorage.getItem('auth_token')
      const tokenIdentity = token ? parseAuthTokenIdentity(token) : null
      let normalizedResponseUser = normalizeUser(response.user)
      if (tokenIdentity?.email && normalizedResponseUser.email && tokenIdentity.email !== normalizedResponseUser.email) {
        const currentUser = await authApi.getCurrentUser()
        normalizedResponseUser = normalizeUser(currentUser)
      }
      if (normalizedResponseUser.email === 'demo1@open1031.ai') {
        localStorage.setItem('demo_identity_verified', 'true')
      }
      setUser((previousUser) => {
        if (!previousUser) return normalizedResponseUser
        if (previousUser.email === 'demo1@open1031.ai') {
          return {
            ...previousUser,
            ...normalizedResponseUser,
            id: previousUser.id,
            email: previousUser.email
          }
        }
        if (previousUser.email && normalizedResponseUser.email && previousUser.email !== normalizedResponseUser.email) {
          return {
            ...previousUser,
            isIdentityVerified: normalizedResponseUser.isIdentityVerified,
            isKycComplete: normalizedResponseUser.isKycComplete
          }
        }
        return {
          ...previousUser,
          ...normalizedResponseUser
        }
      })
      setIsIdentityVerified(normalizedResponseUser.isIdentityVerified || true)
    } catch (error) {
      if (import.meta.env.DEV && user?.email === 'demo1@open1031.ai') {
        localStorage.setItem('demo_identity_verified', 'true')
        setUser((previousUser) => {
          if (!previousUser) return previousUser
          return {
            ...previousUser,
            isIdentityVerified: true,
            isKycComplete: true
          }
        })
        setIsIdentityVerified(true)
        return
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      setIsLoading(true)
      const updatedUser = await authApi.updateProfile(profileData)
      const token = localStorage.getItem('auth_token')
      const tokenIdentity = token ? parseAuthTokenIdentity(token) : null
      let normalizedUser = normalizeUser(updatedUser)
      if (tokenIdentity?.email && normalizedUser.email && tokenIdentity.email !== normalizedUser.email) {
        const currentUser = await authApi.getCurrentUser()
        normalizedUser = normalizeUser(currentUser)
      }
      setUser(normalizedUser)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isIdentityVerified,
    login,
    register,
    logout,
    verifyIdentity,
    updateProfile,
    isLoading,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

